<div class="shell">
  <header class="page-header modern">
    <div class="brand-wrap">
      <div class="brand-left">
        <div class="brand-logo" [class.has-image]="brandMeta?.logo">
          <img
            *ngIf="brandMeta?.logo"
            [src]="brandMeta.logo"
            alt="{{ brandMeta?.brandName || 'Brand' }}"
            class="brand-img"
            loading="lazy"
          />
          <span *ngIf="!brandMeta?.logo" class="brand-fallback">G</span>
        </div>

        <div class="brand-text">
          <div class="brand-company">
            {{ brandMeta?.brandName || brandMeta?.shortName || 'Sovos' }}
          </div>
          <div class="brand-product">e-Invoice Claim Portal</div>
        </div>
      </div>

      <div class="header-right">
        <div class="chip note" role="note">
          <span class="chip-icon" aria-hidden="true">⏱</span>
          <span>Claim within 30 days of invoice date</span>
        </div>
      </div>
    </div>
  </header>

  <main class="card" role="region" aria-labelledby="flow-title">
    <section class="stepper" aria-label="Progress">
      <div class="stepper-track" role="list">
        <div
          class="step-item"
          *ngFor="let s of steps"
          [class.active]="currentStep === s.id"
          [class.done]="currentStep > s.id"
          role="listitem"
        >
          <button
            class="step-button"
            type="button"
            (click)="goToStep(s.id)"
            (keyup)="onStepKeyup($event, s.id)"
            [attr.aria-current]="currentStep === s.id ? 'step' : null"
            [attr.aria-label]="s.title + ': ' + s.subtitle"
            [disabled]="!isStepEnabled(s.id) || submitted"
          >
            <div class="step-circle" aria-hidden="true">{{ s.id }}</div>
            <div class="step-labels">
              <div class="step-title">{{ s.title }}</div>
              <div class="step-subtitle help-text-sm">{{ s.subtitle }}</div>
            </div>
          </button>
        </div>
      </div>

      <div class="stepper-progress" aria-hidden="true">
        <div class="stepper-progress-bar" [style.width]="progress[currentStep]"></div>
      </div>

      <div id="flow-title" class="step-caption help-text">{{ captions[currentStep] }}</div>
    </section>

    <section class="content">
      <!-- SUCCESS SCREEN -->
      <div *ngIf="submitted" class="panel confirmation-panel tw-mt-4">
        <!-- Header -->
        <div class="panel-header">
          <div class="panel-header-title">Invoice Claim Submitted</div>
          <p class="panel-header-subtitle">Your invoice request has been submitted to LHDN for eInvoice generation.</p>
        </div>

        <!-- Success card -->
        <div class="confirmation-card">
          <div class="confirmation-icon">✓</div>

          <h3 class="confirmation-title">Submitted to LHDN</h3>

          <p class="confirmation-sub">
            Reference Document No:
            <strong>{{ review.docNumber || '—' }}</strong>
          </p>

          <!-- Status block -->
          <div class="status-box" *ngIf="claimStatus">
            <span
              class="status-pill"
              [ngClass]="{
                processing: claimStatus === 'PROCESSING',
                valid: claimStatus === 'VALID',
                errored: claimStatus === 'ERRORED' || claimStatus === 'INVALID',
              }"
            >
              {{ claimStatus }}
            </span>

            <p class="status-message error" *ngIf="claimStatus === 'INVALID'">
              {{ claimErrorMessage || 'Invoice rejected by LHDN due to validation errors.' }}
            </p>

            <p class="status-message" *ngIf="claimStatus === 'VALID'">
              Your e-invoice has been successfully generated by LHDN.
            </p>

            <p class="status-message error" *ngIf="claimStatus === 'ERRORED'">
              {{ claimErrorMessage || 'Your e-invoice could not be generated by LHDN.' }}
            </p>
          </div>

          <!-- Actions -->
          <div class="confirmation-actions">
            <button class="btn btn-primary" (click)="checkStatus()" [disabled]="checkingStatus">
              <span *ngIf="!checkingStatus">Check Status</span>
              <span *ngIf="checkingStatus" class="btn-loading"> <span class="spinner"></span> Checking… </span>
            </button>

            <button class="btn btn-secondary" *ngIf="claimStatus === 'VALID'" (click)="printPdf()">Download PDF</button>

            <button class="btn btn-secondary" (click)="newClaim()">Start New Claim</button>
          </div>
        </div>
      </div>

      <!-- STEP 1: Search (three-step mode) -->
      <section class="panel" *ngIf="!skipSearch && currentStep === 1" aria-labelledby="step1-title">
        <header class="panel-header">
          <div id="step1-title" class="panel-header-title !tw-mb-0">Search invoice details</div>
          <p class="help-text tw-mb-3">
            These values come from the QR / invoice. Edit only if they don't match the copy you received.
          </p>
        </header>

        <div class="section">
          <form [formGroup]="invoiceForm" novalidate>
            <div class="grid grid--2">
              <div class="field">
                <label for="supplierTin">Supplier TIN <span class="req">*</span></label>
                <input id="supplierTin" type="text" formControlName="supplierTin" placeholder="C27787744000" />
                <div
                  class="input-error help-text-sm"
                  *ngIf="invoiceForm.controls.supplierTin.touched && invoiceForm.controls.supplierTin.invalid"
                >
                  <span *ngIf="invoiceForm.controls.supplierTin.errors?.required">Supplier TIN is required</span>
                  <span *ngIf="invoiceForm.controls.supplierTin.errors?.maxlength">Max 14 characters</span>
                  <span *ngIf="invoiceForm.controls.supplierTin.errors?.noSpaces">No leading/trailing spaces</span>
                </div>
              </div>

              <div class="field">
                <label for="docNumber">Document Number <span class="req">*</span></label>
                <input id="docNumber" type="text" formControlName="docNumber" placeholder="INV001" />
                <div
                  class="input-error help-text-sm"
                  *ngIf="invoiceForm.controls.docNumber.touched && invoiceForm.controls.docNumber.invalid"
                >
                  <span *ngIf="invoiceForm.controls.docNumber.errors?.required">Document number is required</span>
                  <span *ngIf="invoiceForm.controls.docNumber.errors?.noTrailingSpace">No trailing space allowed</span>
                </div>
              </div>

              <div class="field">
                <label for="docDate">Document Date <span class="req">*</span></label>
                <input
                  id="docDate"
                  type="date"
                  formControlName="docDate"
                  [attr.max]="todayString"
                  [attr.min]="minDateString"
                />
                <div
                  class="input-error help-text-sm"
                  *ngIf="invoiceForm.controls.docDate.touched && invoiceForm.controls.docDate.invalid"
                >
                  <span *ngIf="invoiceForm.controls.docDate.errors?.required">Document date is required</span>
                  <span *ngIf="invoiceForm.controls.docDate.errors?.dateWithin30"
                    >Document date must be within last 30 days</span
                  >
                </div>
              </div>

              <div class="field">
                <label for="totalValue">Total Invoice Value <span class="req">*</span></label>
                <input
                  id="totalValue"
                  type="number"
                  step="0.01"
                  formControlName="totalValue"
                  placeholder="4482.00"
                  onkeydown="return !['e','E','+','-'].includes(event.key)"
                />
                <div
                  class="input-error help-text-sm"
                  *ngIf="invoiceForm.controls.totalValue.touched && invoiceForm.controls.totalValue.invalid"
                >
                  <span *ngIf="invoiceForm.controls.totalValue.errors?.required">Total invoice value is required</span>
                  <span *ngIf="invoiceForm.controls.totalValue.errors?.decimal">Invalid amount</span>
                </div>
              </div>
            </div>
          </form>
        </div>

        <div class="actions">
          <button
            class="btn btn-primary"
            type="button"
            (click)="lookupInvoice()"
            [disabled]="lookupInProgress || !invoiceForm.valid"
          >
            <ng-container *ngIf="!lookupInProgress">Look-up Invoice →</ng-container>
            <ng-container *ngIf="lookupInProgress">Looking up…</ng-container>
          </button>
        </div>
      </section>

      <!-- STEP 2: Invoice Details (Preview) -->
      <!-- ================= STEP 2: INVOICE DETAILS (REVIEW) ================= -->
      <section
        class="panel"
        *ngIf="(isInvoiceDetailsStep(2) && currentStep === 2) || (skipSearch && currentStep === 1)"
        aria-labelledby="step2-title"
      >
        <header class="panel-header">
          <div id="step2-title" class="panel-header-title !tw-mb-0">Invoice preview</div>
          <p class="help-text tw-mb-3">
            Please review the invoice details carefully before proceeding to buyer information.
          </p>
        </header>

        <div class="review-card">
          <!-- ================= TOP SUMMARY ================= -->
          <div class="review-top">
            <div class="review-tile highlight">
              <div class="review-tile-label">Total payable amount</div>
              <div class="review-tile-value amount">
                RM {{ review.invoiceValues?.totalPayableAmount | number: '1.2-2' }}
              </div>
              <div class="review-tile-sub">Including tax</div>
            </div>

            <div class="review-tile">
              <div class="review-tile-label">Invoice number</div>
              <div class="review-tile-value">{{ review.docNumber }}</div>
            </div>

            <div class="review-tile">
              <div class="review-tile-label">Invoice date</div>
              <div class="review-tile-value">{{ review.docDate }}</div>
            </div>
          </div>

          <!-- ================= INVOICE & SUPPLIER ================= -->
          <div class="review-grid-2 invoice-mobile-stack">
            <!-- INVOICE DETAILS -->
            <div class="review-section">
              <div class="review-section-header">
                <div class="review-section-title">Invoice overview</div>
                <div class="review-section-tag">From invoice</div>
              </div>

              <div class="mobile-field">
                <label>Seller TIN</label>
                <div class="value strong">{{ review.supplierTin }}</div>
              </div>

              <div class="mobile-field">
                <label>Invoice number</label>
                <div class="value">{{ review.docNumber }}</div>
              </div>

              <div class="mobile-field">
                <label>Currency</label>
                <div class="value">{{ review.document?.currencyCode || 'MYR' }}</div>
              </div>

              <hr class="mobile-sep" />

              <div class="review-section-header">
                <div class="review-section-title">Amount breakdown</div>
              </div>

              <div class="mobile-field">
                <label>Subtotal (excluding tax)</label>
                <div class="value">
                  {{ review.invoiceValues?.totalInvoiceExcludingTaxAmount | number: '1.2-2' }}
                </div>
              </div>

              <div class="mobile-field">
                <label>Total tax charged</label>
                <div class="value">
                  {{ review.invoiceValues?.invoiceTotalTaxAmount | number: '1.2-2' }}
                </div>
              </div>

              <div class="mobile-field">
                <label>Total payable</label>
                <div class="value strong">
                  {{ review.invoiceValues?.totalPayableAmount | number: '1.2-2' }}
                </div>
              </div>
            </div>

            <!-- SUPPLIER DETAILS -->
            <div class="review-section">
              <div class="review-section-header">
                <div class="review-section-title">Seller details</div>
                <div class="review-section-tag">As registered</div>
              </div>

              <div class="mobile-field">
                <label>Business name</label>
                <div class="value">{{ review.supplier?.supplierName || '—' }}</div>
              </div>

              <div class="mobile-field">
                <label>Seller TIN</label>
                <div class="value">{{ review.supplier?.supplierTin || '—' }}</div>
              </div>

              <div class="mobile-field" *ngIf="review.supplier?.supplierSST">
                <label>SST registration</label>
                <div class="value">{{ review.supplier.supplierSST }}</div>
              </div>

              <div class="mobile-field">
                <label>Contact number</label>
                <div class="value">{{ review.supplier?.supplierContactNumber || '—' }}</div>
              </div>

              <div class="mobile-field">
                <label>Email address</label>
                <div class="value">{{ review.supplier?.supplierEmail || '—' }}</div>
              </div>

              <div class="mobile-field">
                <label>Registered address</label>
                <div class="value review-address">
                  {{ review.supplier?.supplierAddLine0 }}
                  <ng-container *ngIf="review.supplier?.supplierAddLine1">
                    , {{ review.supplier.supplierAddLine1 }}
                  </ng-container>
                  <ng-container *ngIf="review.supplier?.supplierAddLine2">
                    , {{ review.supplier.supplierAddLine2 }}
                  </ng-container>
                  <br />
                  {{ review.supplier?.supplierCity }},
                  {{ review.supplier?.supplierState }}
                  {{ review.supplier?.supplierPostalZone }}
                  <br />
                  {{ review.supplier?.supplierCountry }}
                </div>
              </div>
            </div>
          </div>

          <!-- ================= ITEMS ================= -->
          <div class="review-section" style="margin-top: 12px" *ngIf="review.itemDetails?.length">
            <div class="review-section-header">
              <div class="review-section-title">Goods / services billed</div>
              <div class="review-section-tag">{{ review.itemDetails.length }} item(s)</div>
            </div>

            <div class="items-table-wrapper">
              <table class="items-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Unit price</th>
                    <th>Discount</th>
                    <th>Tax</th>
                    <th>Line total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let it of review.itemDetails; let i = index">
                    <td>{{ it.itemSerialNumber || i + 1 }}</td>
                    <td>{{ it.description }}</td>
                    <td>{{ it.qty }}</td>
                    <td>{{ it.unitPrice }}</td>
                    <td>{{ it.itemDiscountDetails?.[0]?.itemDiscAmount || '0.00' }}</td>
                    <td>{{ it.itemTaxTypeDetails?.[0]?.itemTaxAmount || '0.00' }}</td>
                    <td>{{ it.itemTotalExcludingTax | number: '1.2-2' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- ================= TAX SUMMARY ================= -->
          <div class="review-section" style="margin-top: 12px" *ngIf="review.taxTypeGroupings?.length">
            <div class="review-section-header">
              <div class="review-section-title">Tax summary</div>
            </div>

            <div class="review-rows">
              <div class="review-row" *ngFor="let t of review.taxTypeGroupings">
                <div class="review-label">
                  <strong>Tax category</strong><br />
                  <span class="help-text-sm">{{ t.invoiceTaxTypeGroup }}</span>
                </div>

                <div class="review-value">
                  <strong>Tax charged</strong><br />
                  {{ t.invoiceTaxAmountPerTaxType | number: '1.2-2' }}
                </div>

                <div class="review-value">
                  <strong>Tax exemption</strong><br />
                  0.00
                </div>
              </div>
            </div>
          </div>

          <!-- ================= TOTAL VALUE DETAILS ================= -->
          <div class="review-section" style="margin-top: 12px" *ngIf="review.invoiceValues">
            <div class="review-section-header">
              <div class="review-section-title">Invoice totals</div>
            </div>

            <div class="review-rows">
              <div class="review-row gap-0 tw-mb-2">
                <div class="review-label"><strong>Subtotal (excluding tax)</strong></div>
                <div class="review-value">
                  {{ review.invoiceValues.totalInvoiceExcludingTaxAmount | number: '1.2-2' }}
                </div>
              </div>

              <div class="review-row gap-0 tw-mb-2">
                <div class="review-label"><strong>Total including tax</strong></div>
                <div class="review-value">
                  {{ review.invoiceValues.totalInvoiceIncludingTaxAmount | number: '1.2-2' }}
                </div>
              </div>

              <div class="review-row highlight gap-0 tw-mb-2">
                <div class="review-label"><strong>Amount payable</strong></div>
                <div class="review-value strong">
                  {{ review.invoiceValues.totalPayableAmount | number: '1.2-2' }}
                </div>
              </div>
            </div>
          </div>

          <!-- ================= PAYMENT DETAILS ================= -->
          <div class="review-section" style="margin-top: 12px">
            <div class="review-section-header">
              <div class="review-section-title">Payment information</div>
            </div>

            <div class="mobile-field">
              <label>Payment method</label>
              <div class="value">
                {{ review.paymentDetails?.paymentMethod || '—' }}
              </div>
            </div>
          </div>

          <!-- ================= REASSURANCE ================= -->
          <div class="help-text tw-mt-3">
            If the above invoice details are correct, continue to provide buyer information.
          </div>
        </div>

        <!-- ================= ACTIONS ================= -->
        <div class="actions">
          <button class="btn btn-secondary" type="button" *ngIf="!skipSearch || currentStep > 1" (click)="goBack()">
            ← Back
          </button>

          <button class="btn btn-primary" type="button" (click)="mobileNext()">Next: Buyer details →</button>
        </div>
      </section>

      <!-- full section wrapper (unchanged header/stepper above) -->
      <!-- STEP 3: Buyer details (UPDATED layout only) -->
      <section class="panel" *ngIf="!submitted && currentStep === (skipSearch ? 2 : 3)" aria-labelledby="step3-title">
        <header class="panel-header">
          <div id="step3-title" class="panel-header-title !tw-mb-0">Buyer details</div>
          <p class="panel-header-subtitle help-text">
            Please validate Buyer TIN (click Verify TIN). Validation is required before submitting.
          </p>
        </header>

        <form [formGroup]="buyerForm" novalidate>
          <div class="buyer-section">
            <div class="buyer-row tw-mb-6">
              <div class="buyer-col">
                <label for="buyerName"> Buyer name <span class="req">*</span> </label>

                <input
                  id="buyerName"
                  type="text"
                  formControlName="name"
                  maxlength="300"
                  placeholder="Full name as per ID"
                />

                <div class="input-error help-text-sm">
                  <ng-container *ngIf="buyerForm.controls.name.errors">
                    <span *ngIf="buyerForm.controls.name.errors.required">Required</span>
                    <span *ngIf="buyerForm.controls.name.errors.maxlength">Max 300 characters</span>
                    <span *ngIf="buyerForm.controls.name.errors.noLeadingSpace">No leading space allowed</span>
                    <span *ngIf="buyerForm.controls.name.errors.noTrailingSpace">No trailing space allowed</span>
                  </ng-container>
                </div>
              </div>

              <div class="buyer-col">
                <label for="buyerTin">Buyer TIN</label>

                <input id="buyerTin" type="text" formControlName="buyerTin" maxlength="14" placeholder="C00000000010" />

                <div class="input-error help-text-sm">
                  <ng-container *ngIf="buyerForm.controls.buyerTin.errors">
                    <span *ngIf="buyerForm.controls.buyerTin.errors.maxlength">Max 14 characters</span>
                    <span *ngIf="buyerForm.controls.buyerTin.errors.noLeadingSpace">No leading space allowed</span>
                    <span *ngIf="buyerForm.controls.buyerTin.errors.noTrailingSpace">No trailing space allowed</span>
                  </ng-container>
                </div>

                <div class="input-error help-text-sm" *ngIf="buyerTinError">
                  {{ buyerTinError }}
                </div>

                <div class="tin-verified tw-mt-1" *ngIf="buyerTinValid">
                  <span class="tin-status success">TIN validated ✓</span>
                </div>
              </div>
            </div>

            <div class="buyer-row tw-mb-6">
              <div class="buyer-col">
                <label for="buyerIdType"> ID Type <span class="req">*</span> </label>

                <select id="buyerIdType" formControlName="idType">
                  <option value="NRIC">IC (NRIC)</option>
                  <option value="PASSPORT">Passport</option>
                  <option value="ARMY">Army</option>
                </select>
              </div>

              <div class="buyer-col">
                <label for="buyerId"> ID Number <span class="req">*</span> </label>

                <input
                  id="buyerId"
                  type="text"
                  formControlName="idNumber"
                  maxlength="20"
                  placeholder="Enter ID number"
                />

                <div class="input-error help-text-sm">
                  <ng-container *ngIf="buyerForm.controls.idNumber.errors">
                    <span *ngIf="buyerForm.controls.idNumber.errors.required">ID number is required</span>
                    <span *ngIf="buyerForm.controls.idNumber.errors.pattern">
                      IC / Army ID must be exactly 12 digits
                    </span>
                    <span *ngIf="buyerForm.controls.idNumber.errors.minlength">Passport number is too short</span>
                    <span *ngIf="buyerForm.controls.idNumber.errors.maxlength">Passport number is too long</span>
                    <span *ngIf="buyerForm.controls.idNumber.errors.noLeadingSpace">No leading space allowed</span>
                    <span *ngIf="buyerForm.controls.idNumber.errors.noTrailingSpace">No trailing space allowed</span>
                  </ng-container>
                </div>

                <div class="tw-flex tw-justify-end tw-mt-2" *ngIf="shouldShowVerifyTin()">
                  <button
                    class="btn btn-primary tw-w-1/2"
                    type="button"
                    (click)="validateBuyerTin(true)"
                    [disabled]="buyerTinLoading"
                  >
                    <ng-container *ngIf="!buyerTinLoading">Verify TIN</ng-container>
                    <ng-container *ngIf="buyerTinLoading">Validating…</ng-container>
                  </button>
                </div>
              </div>
            </div>

            <div class="buyer-row tw-mb-6">
              <div class="buyer-col">
                <label for="buyerSst">SST</label>

                <input
                  id="buyerSst"
                  type="text"
                  formControlName="buyerSst"
                  maxlength="20"
                  placeholder="NA or XXX-XXXX-XXXXXXXX"
                  pattern="^(?:NA$|([a-zA-Z0-9]{3}-\d{4}-\d{8}))(?:;(?:[a-zA-Z0-9]{3}-\d{4}-\d{8}))?$"
                  (blur)="buyerForm.controls.buyerSst.markAsTouched()"
                />

                <div class="input-error help-text-sm">
                  <span *ngIf="buyerForm.controls.buyerSst.errors?.pattern">
                    Invalid SST format. Use <strong>NA</strong> or <strong>XXX-XXXX-XXXXXXXX</strong>.
                  </span>
                  <span *ngIf="buyerForm.controls.buyerSst.errors?.noLeadingSpace"> No leading space allowed </span>
                  <span *ngIf="buyerForm.controls.buyerSst.errors?.noTrailingSpace"> No trailing space allowed </span>
                </div>
              </div>

              <div class="buyer-col">
                <label for="buyerContact"> Contact number <span class="req">*</span> </label>

                <input id="buyerContact" type="tel" formControlName="contact" maxlength="15" placeholder="+60" />

                <div class="input-error help-text-sm">
                  <ng-container *ngIf="buyerForm.controls.contact.errors">
                    <span *ngIf="buyerForm.controls.contact.errors.required">Required</span>
                    <span *ngIf="buyerForm.controls.contact.errors.pattern">Enter valid contact number</span>
                    <span *ngIf="buyerForm.controls.contact.errors.noLeadingSpace">No leading space allowed</span>
                  </ng-container>
                </div>
              </div>
            </div>

            <div class="buyer-row tw-mb-6">
              <div class="buyer-col">
                <label for="buyerEmail">Email</label>

                <input
                  id="buyerEmail"
                  type="email"
                  formControlName="email"
                  maxlength="320"
                  placeholder="name@example.com"
                />

                <div class="input-error help-text-sm">
                  <ng-container *ngIf="buyerForm.controls.email.errors">
                    <span *ngIf="buyerForm.controls.email.errors.email">Invalid email</span>
                    <span *ngIf="buyerForm.controls.email.errors.maxlength">Max 320 characters</span>
                    <span *ngIf="buyerForm.controls.email.errors.noLeadingSpace">No leading space allowed</span>
                    <span *ngIf="buyerForm.controls.email.errors.pattern">Invalid email</span>
                  </ng-container>
                </div>
              </div>
            </div>

            <div class="section address-grid tw-mt-6">
              <div class="grid grid--2 tw-mb-4">
                <div class="field">
                  <label>Address line 1 <span class="req">*</span></label>
                  <input type="text" formControlName="addr1" />
                  <div class="input-error help-text-sm">
                    <span *ngIf="buyerForm.controls.addr1.errors?.required">Required</span>
                    <span *ngIf="buyerForm.controls.addr1.errors?.noLeadingSpace">No leading space allowed</span>
                  </div>
                </div>

                <div class="field">
                  <label>Address line 2</label>
                  <input type="text" formControlName="addr2" />
                </div>
              </div>

              <div class="grid grid--2 tw-mb-4">
                <div class="field">
                  <label>Address line 3</label>
                  <input type="text" formControlName="addr3" />
                </div>

                <div class="field">
                  <label>Country <span class="req">*</span></label>
                  <select formControlName="country">
                    <option value="">- Select -</option>
                    <option *ngFor="let c of countryList" [value]="c.value">
                      {{ c.label }}
                    </option>
                  </select>
                  <div class="input-error help-text-sm">
                    <span *ngIf="buyerForm.controls.country.errors?.required">Required</span>
                  </div>
                </div>
              </div>

              <div class="grid grid--2 tw-mb-4">
                <div class="field">
                  <label>State <span class="req">*</span></label>
                  <select formControlName="state">
                    <option value="">- Select -</option>
                    <option *ngFor="let s of stateData" [value]="s.State">
                      {{ s.State }}
                    </option>
                  </select>
                  <div class="input-error help-text-sm">
                    <span *ngIf="buyerForm.controls.state.errors?.required">Required</span>
                  </div>
                </div>

                <div class="field">
                  <label>City <span class="req">*</span></label>
                  <input type="text" formControlName="city" />
                  <div class="input-error help-text-sm">
                    <span *ngIf="buyerForm.controls.city.errors?.required">Required</span>
                    <span *ngIf="buyerForm.controls.city.errors?.noLeadingSpace">No leading space allowed</span>
                  </div>
                </div>
              </div>

              <div class="grid grid--2 tw-mb-4">
                <div class="field">
                  <label>Postal zone</label>
                  <input type="text" formControlName="postal" placeholder="Postal Zone" />
                </div>
              </div>
            </div>
          </div>
        </form>
      </section>

      <div class="actions desktop" *ngIf="!submitted && currentStep === (skipSearch ? 2 : 3)">
        <button
          class="btn btn-primary tw-ml-auto"
          type="button"
          (click)="submitClaim()"
          [disabled]="isStickyDisabled()"
        >
          <ng-container *ngIf="!isSubmitting" class="actions tw-hidden lg:tw-flex desktop">Submit to LHDN</ng-container>
          <ng-container *ngIf="isSubmitting" class="actions tw-hidden lg:tw-flex desktop">
            <span class="spinner" aria-hidden="true"></span>
            Submitting…
          </ng-container>
        </button>
      </div>
    </section>
  </main>
  <footer class="app-footer">
    <div class="footer-inner">
      <!-- Left -->
      <div class="footer-left">
        <img
          class="footer-logo"
          [src]="logoUrl"
          alt="Sovos"
          loading="lazy"
          (error)="onFooterLogoError = true"
          *ngIf="!onFooterLogoError"
        />
      </div>

      <!-- Center -->
      <nav class="footer-links" aria-label="Footer links">
        <a href="https://myeinvois.my/privacy-policy/" target="_blank">Privacy</a>
        <span class="footer-sep">•</span>
        <a href="https://myeinvois.my/cookies-policy/" target="_blank">Cookies</a>
        <span class="footer-sep">•</span>
        <a href="https://myeinvois.my/terms-conditions/" target="_blank">Terms</a>
      </nav>

      <!-- Right -->
      <div class="footer-right">© {{ currentYear }} Sovos</div>
    </div>
  </footer>
</div>

<!-- sticky footer button (mobile) -->
<div class="sticky-footer" role="region" aria-hidden="false">
  <div class="sticky-text help-text-sm">{{ stickyCaptions[currentStep] }}</div>
  <button class="btn btn-primary" (click)="mobileNext()" [disabled]="isStickyDisabled() || isSubmitting || submitted">
    <ng-container *ngIf="!isSubmitting">
      {{
        submitted
          ? 'Submitted'
          : skipSearch && currentStep === 1
            ? 'Next'
            : currentStep === (skipSearch ? 2 : 3)
              ? 'Submit'
              : 'Next'
      }}
    </ng-container>

    <ng-container *ngIf="isSubmitting">
      <span class="spinner" aria-hidden="true"></span>
      Submitting…
    </ng-container>
  </button>
</div>
